import { useEffect, useState } from 'react';
import { loadNetworkRevenue, setFixedCosts, type NetworkRevenue, type RevenuePeriod } from '../../lib/revenueService';

// Écran super admin — CA & Bénéfice temps réel par jour / semaine / mois + coûts fixes éditables.

const eur = (cents: number) => `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const PERIODS: { key: RevenuePeriod; label: string }[] = [
  { key: 'day', label: 'Jour' },
  { key: 'week', label: 'Semaine' },
  { key: 'month', label: 'Mois' },
];
const PERIOD_DESC: Record<RevenuePeriod, string> = {
  day: "aujourd'hui",
  week: '7 derniers jours',
  month: 'ce mois',
};

export default function RevenuePanel() {
  const [period, setPeriod] = useState<RevenuePeriod>('month');
  const [d, setD] = useState<NetworkRevenue | null>(null);
  const [loading, setLoading] = useState(true);
  const [costInput, setCostInput] = useState('');
  const [savingCost, setSavingCost] = useState(false);
  const [msg, setMsg] = useState('');

  const load = (p: RevenuePeriod) => {
    setLoading(true);
    loadNetworkRevenue(p).then((res) => {
      setD(res);
      if (res.ok) setCostInput((res.fixed_costs_cents / 100).toFixed(2));
      setLoading(false);
    });
  };

  useEffect(() => { load(period); }, [period]);

  const saveCosts = async () => {
    const euros = parseFloat((costInput || '').replace(',', '.'));
    if (Number.isNaN(euros) || euros < 0) { setMsg('Montant invalide.'); return; }
    setSavingCost(true);
    setMsg('');
    const res = await setFixedCosts(Math.round(euros * 100));
    setSavingCost(false);
    if (res.ok) load(period);
    else setMsg(res.error ?? 'Échec.');
  };

  if (loading) return <p className="opacity-70">Chargement…</p>;
  if (!d || !d.ok) {
    return (
      <div className="max-w-3xl rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {d?.error?.includes('network_settings') ? 'Base non à jour — exécute sql/46_network_settings.sql.' : (d?.error ?? 'Erreur.')}
      </div>
    );
  }

  const net = d.net_period_cents;
  const desc = PERIOD_DESC[period];

  return (
    <div className="max-w-4xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-slate-900">💶 CA &amp; Bénéfice — temps réel</h2>
          <p className="text-sm text-slate-500">Bénéfice net ({desc}) · récurrent proratisé + commissions live.</p>
        </div>
        <div className="inline-flex overflow-hidden rounded-xl border border-slate-300">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-4 py-2 text-sm font-semibold ${period === p.key ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cartes principales */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-2xl p-5 text-white" style={{ background: 'linear-gradient(135deg,#2563EB,#1E40AF)' }}>
          <div className="text-xs uppercase opacity-80">CA ({desc})</div>
          <div className="text-3xl font-black">{eur(d.ca_period_cents)}</div>
          <div className="text-xs opacity-80">récurrent + commissions</div>
        </div>
        <div className="rounded-2xl p-5 text-white" style={{ background: net >= 0 ? 'linear-gradient(135deg,#059669,#047857)' : 'linear-gradient(135deg,#DC2626,#991B1B)' }}>
          <div className="text-xs uppercase opacity-80">Bénéfice net ({desc})</div>
          <div className="text-3xl font-black">{eur(net)}</div>
          <div className="text-xs opacity-80">CA − coûts fixes</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-xs uppercase text-slate-500">Net projeté / an</div>
          <div className="text-3xl font-black text-slate-900">{eur(d.net_year_cents)}</div>
          <div className="text-xs text-slate-500">MRR − coûts fixes, ×12</div>
        </div>
      </div>

      {/* Détail */}
      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-3 text-xs font-bold uppercase text-slate-500">Détail ({desc})</div>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b border-slate-100">
              <td className="py-2 text-slate-600">Récurrent (abos {d.active_subs} + options){period !== 'month' ? ' — proratisé' : ''}</td>
              <td className="py-2 text-right font-semibold text-slate-800">{eur(d.recurring_period_cents)}</td>
            </tr>
            <tr className="border-b border-slate-100">
              <td className="py-2 text-slate-600">Commissions encaissées (Stripe)</td>
              <td className="py-2 text-right font-semibold text-emerald-700">{eur(d.commission_encaisse_cents)}</td>
            </tr>
            <tr className="border-b border-slate-100">
              <td className="py-2 text-slate-600">Commissions à recouvrer (sur place)</td>
              <td className="py-2 text-right font-semibold text-amber-700">{eur(d.commission_recouvrer_cents)}</td>
            </tr>
            <tr className="border-b border-slate-200">
              <td className="py-2 font-bold text-slate-900">CA total ({desc})</td>
              <td className="py-2 text-right font-black text-slate-900">{eur(d.ca_period_cents)}</td>
            </tr>
            <tr>
              <td className="py-2 text-slate-600">− Coûts fixes ({desc})</td>
              <td className="py-2 text-right font-semibold text-red-600">−{eur(d.fixed_period_cents)}</td>
            </tr>
            <tr>
              <td className="py-2 font-bold text-slate-900">= Bénéfice net</td>
              <td className={`py-2 text-right font-black ${net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{eur(net)}</td>
            </tr>
          </tbody>
        </table>
        {period !== 'month' && (
          <p className="mt-2 text-[11px] text-slate-400">Récurrent et coûts fixes proratisés (mois ÷ 30 × période). Les commissions sont les montants réels de la période.</p>
        )}
      </div>

      {/* Coûts fixes éditables */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-2 text-xs font-bold uppercase text-slate-500">Coûts fixes mensuels</div>
        <p className="mb-3 text-xs text-slate-500">Hébergement, Stripe, Qonto, outils… Total mensuel pour calculer le bénéfice net.</p>
        <div className="flex items-center gap-2">
          <input
            type="number" min="0" step="0.01" value={costInput}
            onChange={(e) => setCostInput(e.target.value)}
            className="w-40 rounded-lg border border-slate-300 px-3 py-2 text-right text-sm"
          />
          <span className="text-sm text-slate-500">€ / mois</span>
          <button
            onClick={saveCosts} disabled={savingCost}
            className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {savingCost ? '…' : 'Enregistrer'}
          </button>
        </div>
        {msg && <div className="mt-2 text-sm text-red-600">{msg}</div>}
      </div>
    </div>
  );
}
